import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Save, Info } from "lucide-react";
import { PageHeader } from "@/components/erp";
import { GenelBilgiler } from "@/components/satis/GenelBilgiler";
import { EkBilgiler } from "@/components/satis/EkBilgiler";
import { UrunGiris, UrunListe, Toplamlar } from "@/components/satis/UrunAlanlari";
import { useAuth } from "@/context/AuthContext";
import { BELGE_TUR, bosBelge, hesapla, govde } from "@/lib/satisBelge";
import { barkodEslestir, bip } from "@/components/BarkodOkut";
import api, { errMsg } from "@/lib/api";

const UYARI_IRS = ["Sevk irsaliyeleri temelde ürün hakkında miktar, birim bilgisini içerecek şekilde düzenlendiğinden, ürün satırlarına girdiğiniz vergi, iskonto v.b. veriler irsaliyede gözükmeyecektir.",
  "İrsaliye oluşturulduğu anda ürünleriniz stoklarınızdan eksilmektedir. İrsaliyeye ait faturanızın iki kere stoktan düşmemesi için, faturanızı fatura oluştur ekranından değil, Satış İrsaliyeleri listesinden 'İrsaliyeleri Faturada Birleştir' ile oluşturun."];
const Bilgi = ({ children }) => <div className="flex gap-3 items-start rounded-xl border-l-4 border-blue-500 bg-blue-500/10 px-4 py-3 text-sm"><Info className="w-5 h-5 text-blue-400 shrink-0" />{children}</div>;

const ekleriGonder = async (tur, id, notlar, dosyalar) => {
  for (const n of notlar) { try { await api.post("/kayit-notlari", { ...n, tur, kayit_id: id, grup: "sirket", hatirlatma: null }); } catch (e) { toast.error(`Not: ${errMsg(e)}`); } }
  for (const d of dosyalar) {
    const fd = new FormData(); fd.append("tur", tur); fd.append("kayit_id", id); fd.append("dosya", d);
    try { await api.post("/kayit-dosyalari", fd); } catch (e) { toast.error(`${d.name}: ${errMsg(e)}`); }
  }
};

const useTermin = (tur, satirlar) => {
  const [t, setT] = useState(null);
  const anahtar = JSON.stringify(satirlar.map((s) => [s.urun_id, s.miktar]));
  useEffect(() => {
    if (tur !== "siparis" || !satirlar.length) { setT(null); return; }
    const z = setTimeout(() => api.post("/siparisler/termin-onizle", { kalemler: satirlar.map((s) => ({ urun_id: s.urun_id, miktar: Number(s.miktar) })) }).then((r) => setT(r.data)).catch(() => setT(null)), 400);
    return () => clearTimeout(z);
  }, [anahtar]); // eslint-disable-line react-hooks/exhaustive-deps
  return t;
};

export default function SatisBelgeYeni({ tur }) {
  const cfg = BELGE_TUR[tur];
  const { ayar } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [f, setF] = useState(() => ({ ...bosBelge(ayar, tur), ...(params.get("cari") ? { cari_id: params.get("cari") } : {}) }));
  const [cariler, setCariler] = useState([]);
  const [cari, setCari] = useState(null);
  const [fl, setFl] = useState([]);
  const [projeler, setProjeler] = useState([]);
  const [seriNo, setSeriNo] = useState("");
  const [notlar, setNotlar] = useState([]);
  const [dosyalar, setDosyalar] = useState([]);
  const [duzenle, setDuzenle] = useState(null);
  const [busy, setBusy] = useState(false);
  const sonKur = useRef(1);
  const set = useCallback((k, v) => setF((s) => ({ ...s, [k]: v })), []);
  const cariYukle = useCallback(() => api.get("/cariler").then((r) => setCariler(r.data.filter((c) => c.tip !== "tedarikci" && c.aktif !== false))), []);
  useEffect(() => {
    cariYukle();
    api.get("/projeler").then((r) => setProjeler(r.data)).catch(() => {});
    api.get(`/belge-no-onizle/${tur}`).then((r) => setSeriNo(r.data.no));
    window.addEventListener("focus", cariYukle);
    return () => window.removeEventListener("focus", cariYukle);
  }, [tur, cariYukle]);
  useEffect(() => {
    if (!f.cari_id) return;
    Promise.all([api.get(`/cariler/${f.cari_id}`), api.get(`/cariler/${f.cari_id}/fiyat-listesi`)]).then(([c, l]) => {
      const pb = c.data.para_birimi || "TRY";
      const adr = c.data.adresler?.[0] || c.data;
      setCari(c.data); setFl(l.data.kalemler); sonKur.current = l.data.kur || 1;
      setF((s) => ({ ...s, para_birimi: pb, takip_para_birimi: pb, kur: l.data.kur || 1, satirlar: [], teslim_adresi: [adr.adres, adr.ilce, adr.il, adr.ulke].filter(Boolean).join(" - ") }));
    });
  }, [f.cari_id]);
  const kurDegis = (p) => {
    const k = p === "TRY" ? 1 : Number(ayar?.kurlar?.[p] || 0);
    const eski = sonKur.current;
    if (k > 0) sonKur.current = k;
    setF((s) => ({ ...s, para_birimi: p, kur: k, satirlar: k > 0 ? s.satirlar.map((x) => ({ ...x, birim_fiyat: Number(((x.birim_fiyat * eski) / k).toFixed(4)) })) : s.satirlar }));
  };
  const flPara = useMemo(() => fl.map((x) => ({ ...x, fiyat: Number(((x.fiyat_try || 0) / (f.kur || 1)).toFixed(4)) })), [fl, f.kur]);
  const h = hesapla(f);
  const termin = useTermin(tur, f.satirlar);
  const satirEkle = (s) => { set("satirlar", duzenle != null ? f.satirlar.map((x, i) => (i === duzenle ? s : x)) : [...f.satirlar, s]); setDuzenle(null); };
  const barkodla = async (kod) => {
    if (!f.cari_id) { bip(false); return toast.error("Önce cari seçin"); }
    const m = barkodEslestir(flPara, kod);
    if (!m) {
      bip(false);
      const u = await api.get(`/barkod/${encodeURIComponent(kod)}`).then((r) => r.data).catch(() => null);
      return toast.error(u ? `${u.kod} · ${u.ad} satış listesinde yok (hammadde/pasif olabilir)` : `${kod} barkodlu ürün bulunamadı`);
    }
    bip(true);
    const i = f.satirlar.findIndex((s) => s.urun_id === m.x.urun_id);
    set("satirlar", i >= 0 ? f.satirlar.map((s, n) => (n === i ? { ...s, miktar: +(Number(s.miktar) + m.carpan).toFixed(4) } : s))
      : [...f.satirlar, { urun_id: m.x.urun_id, miktar: m.carpan, birim_fiyat: m.x.fiyat ?? 0, kdv_orani: m.x.kdv_orani ?? 20, aciklama: "" }]);
    toast.success(`+${m.carpan} ${m.x.birim} · ${m.x.kod}${m.birim !== m.x.birim ? ` (1 ${m.birim})` : ""}`);
  };
  const kaydet = async () => {
    if (!f.cari_id) return toast.error("Cari seçin");
    if (!f.satirlar.length) return toast.error("En az bir ürün satırı ekleyin");
    setBusy(true);
    try {
      const { data } = await api.post(cfg.uc, govde(tur, f));
      await ekleriGonder(cfg.kayitTur, data.id, notlar, dosyalar);
      toast.success(`${data.no} kaydedildi`);
      nav(cfg.git(data));
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div data-testid={`belge-yeni-${tur}`} className="space-y-4 pb-10">
      <PageHeader title={cfg.baslik}>
        <button data-testid="belge-kaydet" disabled={busy} onClick={kaydet} className="inline-flex items-center gap-2 px-5 h-10 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold disabled:opacity-50">Kaydet<Save className="w-4 h-4" /></button>
      </PageHeader>
      <div className="grid xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2"><GenelBilgiler cfg={cfg} f={f} set={set} cariler={cariler} cari={cari} seriNo={seriNo} kurDegis={kurDegis} onHizliCari={() => window.open("/cariler/yeni?kategori=genel", "_blank")} /></div>
        <EkBilgiler f={f} set={set} projeler={projeler} notlar={notlar} setNotlar={setNotlar} dosyalar={dosyalar} setDosyalar={setDosyalar} />
      </div>
      {tur === "irsaliye" && UYARI_IRS.map((m) => <Bilgi key={m}>{m}</Bilgi>)}
      <UrunGiris key={duzenle ?? "yeni"} fl={flPara} para={f.para_birimi} duzenlenen={duzenle != null ? f.satirlar[duzenle] : null} onEkle={satirEkle} onBarkod={barkodla} />
      <UrunListe satirlar={f.satirlar} fl={fl} para={f.para_birimi} termin={tur === "siparis" ? termin || [] : null} onDuzenle={setDuzenle}
        onSil={(idx) => { set("satirlar", f.satirlar.filter((_, i) => !idx.includes(i))); setDuzenle(null); }} />
      <Toplamlar f={f} set={set} h={h} />
    </div>
  );
}
